/* eslint-disable react/display-name */

import { memo, useState } from 'react';

import { Button, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookFormResolver, Text } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnGroup, BtnToolbar, Col, Row } from '@dec/shared';
import { type ColDef } from 'ag-grid-community';
import { ActionsGrid } from 'app/components/grid';
import { type ActionColumn } from 'app/components/grid/actions-column';
import GridApplication from 'app/components/grid/gridApplication';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { BaseUrlTaxpayer } from '@/app/modules/taxes/services/tax.service';

import { factoryYup } from '../../../core/factory-yup';
import { useCatalogValueList } from '../../configuration/catalogs/hooks/useCatalogValueList';
import { catalogName } from '../../configuration/catalogs/models/Catalog';
import { useCountries } from '../../Reports/TaxReturnList/hooks';
import { type TaxpayerRequestPaginatedResponse } from '../../Taxpayer/TaxpayerRequestEvaluation/types';
const yup = factoryYup();
const validations = yup.object().shape({
  options: yup.object().nullable().required()
});

export const NonCompliancePaymentsTaxpayers = memo(() => {
  const { data: countries } = useCountries();

  const { data: dataPerson } = useCatalogValueList(catalogName.personTypes);

  const navigate = useNavigate();
  const location = useLocation();
  const url = location.pathname;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  const [search, setSearch] = useState({ onlyWithNonCompliancePayment: true });
  const values = [
    {
      id: 'ommited',
      name: t('collections.label.unpaid')
    },
    {
      id: 'all',
      name: t('common.label.all')
    }
  ];
  const onSearch = (values: any) => {
    values.country = values.countryCatalog?.code;
    values.personType = values.personTypeCatalog?.code;
    values.onlyWithNonCompliancePayment = values.options?.id === 'ommited';
    setSearch(values);
  };

  const actions: Array<ActionColumn> = [
    {
      icon: Icon.Search,
      variant: 'secondary',
      onClick: (row: TaxpayerRequestPaginatedResponse) => {
        navigate(`${url}/${row.id}`);
      }
    }
  ];

  const colDef: Array<ColDef> = [
    {
      headerName: t('common.label.actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    { headerName: t('config.tin'), field: 'nit' },
    {
      headerName: t('taxpayer-report.label.name'),
      field: 'name'
    },
    {
      headerName: t('taxpayer-report.label.personType'),
      field: 'taxpayerTypeModel.name',
      valueGetter: (params: any) => {
        return params.data.taxpayerTypeModel ? params.data.taxpayerTypeModel.name : '';
      }
    },
    {
      headerName: t('taxpayer-report.label.email'),
      field: 'email'
    }
  ];

  return (
    <>
      <PageTitle title={t('non-compliances.title.nonCompliancesPayments')} />
      <HookFormResolver
        validations={validations}
        onSubmit={onSearch}
        initialValues={{ options: values[0] }}
      >
        {({ formState: { errors }, reset, setValue, control, clearErrors }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Text label={t('config.tin')} name="nit" />
                </Col>
                <Col>
                  <Text label={t('taxpayer-report.label.name')} name="name" />
                </Col>
              </Row>
              <Row>
                <Col md={6} lg={6} xl={6}>
                  <Combobox
                    code="id"
                    display="name"
                    data={countries}
                    label={t('taxpayer-report.label.county')}
                    name="countryCatalog"
                  />
                </Col>
                <Col>
                  <Combobox
                    code="id"
                    display="name"
                    data={dataPerson}
                    label={t('taxpayer-report.label.personType')}
                    name="personTypeCatalog"
                  />
                </Col>
                <Col>
                  <Combobox
                    code="id"
                    display="name"
                    data={values}
                    label={t('common.label.see')}
                    name="options"
                  />
                </Col>
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    variant={'secondary'}
                    type={'button'}
                    onClick={() => {
                      setValue('personTypeCatalog', null);
                      setValue('countryCatalog', null);
                      setValue('name', null);
                      setValue('nit', null);
                      setValue('options', values[0]);
                      setSearch({ onlyWithNonCompliancePayment: true });
                      clearErrors();
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
      <hr></hr>
      <Row>
        <Col>
          <GridApplication
            columnDefs={colDef}
            url={`${BaseUrlTaxpayer}/with-non-compliances-payments`}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
});
